export const MAX_ITERATIONS = 1024;
export const SMOOTHING_STEPS = 3;
export const NEWTON_EPS_SQ = 1e-4;
export const NEWTON_SMOOTHING_STEPS = 1;
export const SQRT3_2 = 0.8660254037844386;
export const NEWTON_MODE_CUBIC = 1;
export const NEWTON_MODE_QUARTIC = 2;
export const NEWTON_MODE_SEXTIC = 3;
export const NEWTON_MODE_CENTERED_QUARTIC = 4;
export const NEWTON_MODE_SMALE = 5;
export const SMALE_R0X = -1.7692923542386314;
export const SMALE_R1X = 0.8846461771193157;
export const SMALE_R1Y = 0.5897428050222054;
export const SMALE_K0_SQ = 0.5157193329351983;
export const SMALE_K1_SQ = 0.9894035434615143;
const CBRT9 = 2.080083823051904;
const LOG4_INV = 1 / Math.log(4);
export const NEWTON_ROOTS = Object.freeze([
  Object.freeze([1, 0]),
  Object.freeze([-0.5, SQRT3_2]),
  Object.freeze([-0.5, -SQRT3_2]),
]);
export const SPLIT_ONE_THIRD = Object.freeze([0.3333333432674408, -9.934107758624481e-9]);
export const SPLIT_SQRT3_2 = Object.freeze([0.8660253882408142, 1.554362445248379e-8]);

const LOG2_INV = 1 / Math.log(2);
const LOG_NEWTON_EPS_SQ = Math.log(NEWTON_EPS_SQ);

const STRIPE_BAILOUT = 10000;
const LOG_STRIPE_BAILOUT = 0.5 * Math.log(STRIPE_BAILOUT);
const ORBIT_TRAP_BAILOUT = 256;
const LOG_ORBIT_TRAP_BAILOUT = 0.5 * Math.log(ORBIT_TRAP_BAILOUT);
const STRIPE_FREQUENCY = 6;

export const PATTERN_MODES = new Set([
  'stripe',
  'binary_cells',
  'normal_3d',
  'pickover_stalks',
  'cellular_web',
  'equipotential_rings',
]);

export function isPatternMode(mode) {
  return typeof mode === 'string' && PATTERN_MODES.has(mode);
}

export function parseNewtonC(param) {
  if (!Array.isArray(param) || param.length < 2) return null;
  let ax = Number(param[0]);
  const ay = Number(param[1]);
  if (!Number.isFinite(ax) || !Number.isFinite(ay)) return null;
  if (ax === 0 && ay === 0) ax = 1;
  const rawMode = param.length >= 3 ? Number(param[2]) : NEWTON_MODE_CUBIC;
  const mode = Number.isInteger(rawMode) && rawMode >= NEWTON_MODE_CUBIC && rawMode <= NEWTON_MODE_SMALE
    ? rawMode
    : NEWTON_MODE_CUBIC;
  return [ax, ay, mode];
}

export const parseJuliaC = parseNewtonC;

export function computeNewtonSmoothMu(iteration, x, y, bestK, ax = 1, ay = 0, mode = NEWTON_MODE_CUBIC, bestD2 = null) {
  const linMag2 = (1 - ax) * (1 - ax) + ay * ay;
  if (mode !== NEWTON_MODE_CUBIC) {
    const d2 = Math.max(1e-36, Number.isFinite(bestD2) ? bestD2 : x * x + y * y);
    if (linMag2 > 1e-4 && linMag2 < 0.9999) {
      const frac = Math.max(0, Math.min(1, Math.log(Math.max(1e-30, d2) / NEWTON_EPS_SQ) / Math.log(linMag2)));
      return Math.max(0, iteration - frac);
    }
    if (mode === NEWTON_MODE_CENTERED_QUARTIC && bestK === 3) {
      const logRatio4 = Math.max(1, Math.log(Math.max(1e-36, CBRT9 * d2)) / Math.log(CBRT9 * NEWTON_EPS_SQ));
      return Math.max(0, iteration - Math.log(logRatio4) * LOG4_INV);
    }
    const k2 = mode === NEWTON_MODE_QUARTIC ? 2.25
      : mode === NEWTON_MODE_SEXTIC ? 6.25
      : mode === NEWTON_MODE_CENTERED_QUARTIC ? 4.0
      : (bestK === 0 ? SMALE_K0_SQ : SMALE_K1_SQ);
    const logRatio = Math.max(1, Math.log(Math.max(1e-36, k2 * d2)) / Math.log(k2 * NEWTON_EPS_SQ));
    return Math.max(0, iteration - Math.log(logRatio) * LOG2_INV);
  }
  const rx = NEWTON_ROOTS[bestK][0];
  const ry = NEWTON_ROOTS[bestK][1];
  const dx = x - rx;
  const dy = y - ry;
  let ux = rx * dx + ry * dy;
  let uy = rx * dy - ry * dx;
  if (linMag2 > 1e-4 && linMag2 < 0.9999) {
    const d2 = Math.max(1e-30, ux * ux + uy * uy);
    const frac = Math.max(0, Math.min(1, Math.log(d2 / NEWTON_EPS_SQ) / Math.log(linMag2)));
    return Math.max(0, iteration - frac);
  }
  for (let s = 0; s < NEWTON_SMOOTHING_STEPS; s++) {
    const u2x = ux * ux - uy * uy;
    const u2y = 2 * ux * uy;
    const numX = u2x * (3 + 2 * ux) - u2y * (2 * uy);
    const numY = u2x * (2 * uy) + u2y * (3 + 2 * ux);
    const opux = 1 + ux;
    const opuy = uy;
    const denX = 3 * (opux * opux - opuy * opuy);
    const denY = 6 * opux * opuy;
    const denMag2 = Math.max(1e-36, denX * denX + denY * denY);
    ux = (numX * denX + numY * denY) / denMag2;
    uy = (numY * denX - numX * denY) / denMag2;
  }
  const uMag2 = Math.max(1e-36, ux * ux + uy * uy);
  const logRatio = Math.max(1, Math.log(uMag2) / LOG_NEWTON_EPS_SQ);
  return Math.max(0, iteration + NEWTON_SMOOTHING_STEPS - Math.log(logRatio) * LOG2_INV);
}

function isCardioidOrBulb(x0, y0) {
  const xm = x0 - 0.25;
  const y20 = y0 * y0;
  const q = xm * xm + y20;
  const xp1 = x0 + 1;
  return q * (q + xm) <= 0.25 * y20 || xp1 * xp1 + y20 <= 0.0625;
}

function finalizeEscape(result, iteration, x, y, x2, y2, x0, y0, useMu) {
  result.iterations = iteration;
  result.escaped = x2 + y2 > 4;
  result.mu = 0;
  if (result.root !== undefined) delete result.root;
  if (result.escaped && useMu) {
    // Refine the escaped orbit before evaluating the continuous count. At
    // radius 2 alone, the approximation still produces visible contour seams.
    for (let i = 0; i < SMOOTHING_STEPS; i++) {
      y = 2 * x * y + y0;
      x = x2 - y2 + x0;
      x2 = x * x;
      y2 = y * y;
    }
    result.mu = iteration + SMOOTHING_STEPS + 1 - Math.log(0.5 * Math.log(x2 + y2)) * LOG2_INV;
  }
  return result;
}

// Shared by the main thread and workers. Keep the shader's equivalent in sync.
// A budget of N permits N recurrence steps and checks z_N for strict escape.
// The separate flag matters when escape occurs on the last permitted step.
export function iterateUntilEscape(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true, juliaC = null) {
  const newton = parseNewtonC(juliaC);
  if (newton) {
    const ax = newton[0], ay = newton[1], mode = newton[2];
    const unrelaxed = (ax - 1) * (ax - 1) + ay * ay <= 1e-6;
    // Relaxed parameter with |1 - a| >= 1 (e.g. a = 2) has repelling roots;
    // starting at the pole z0 = 0 never converges to any cube root of unity.
    if (maxIterations <= 0) {
      result.iterations = 0;
      result.escaped = false;
      result.mu = 0;
      result.root = 0;
      return result;
    }
    let x = x0, y = y0;
    let iteration = 0;
    while (iteration < maxIterations) {
      const x2 = x * x, y2 = y * y;
      const r2 = x2 + y2;
      if (!Number.isFinite(r2)) {
        result.iterations = Math.ceil(maxIterations);
        result.escaped = false;
        result.mu = 0;
        result.root = 0;
        return result;
      }
      let sx = 0, sy = 0;
      if (mode === NEWTON_MODE_CUBIC) {
        if (r2 <= 1e-28) {
          result.iterations = Math.ceil(maxIterations);
          result.escaped = false;
          result.mu = 0;
          result.root = 0;
          return result;
        }
        const r4 = Math.max(1e-30, r2 * r2);
        sx = (x - (x2 - y2) / r4) / 3;
        sy = (y + (2 * x * y) / r4) / 3;
      } else if (mode === NEWTON_MODE_QUARTIC) {
        if (r2 <= 1e-20) {
          result.iterations = Math.ceil(maxIterations);
          result.escaped = false;
          result.mu = 0;
          result.root = 0;
          return result;
        }
        const ux = x / r2, uy = y / r2;
        const ux2 = ux * ux, uy2 = uy * uy;
        sx = 0.25 * (x - ux * (ux2 - 3 * uy2));
        sy = 0.25 * (y + uy * (3 * ux2 - uy2));
      } else if (mode === NEWTON_MODE_SEXTIC) {
        if (r2 <= 1e-12) {
          result.iterations = Math.ceil(maxIterations);
          result.escaped = false;
          result.mu = 0;
          result.root = 0;
          return result;
        }
        const ux = x / r2, uy = y / r2;
        const ux2 = ux * ux, uy2 = uy * uy;
        const ux4 = ux2 * ux2, uy4 = uy2 * uy2, ux2y2 = ux2 * uy2;
        sx = (x - ux * (ux4 - 10 * ux2y2 + 5 * uy4)) / 6;
        sy = (y + uy * (5 * ux4 - 10 * ux2y2 + uy4)) / 6;
      } else if (mode === NEWTON_MODE_CENTERED_QUARTIC) {
        const z3x = x * (x2 - 3 * y2);
        const z3y = y * (3 * x2 - y2);
        const denX = 4 * z3x - 1;
        const denY = 4 * z3y;
        const den = denX * denX + denY * denY;
        if (den <= 1e-28 || !Number.isFinite(den)) {
          result.iterations = Math.ceil(maxIterations);
          result.escaped = false;
          result.mu = 0;
          result.root = 0;
          return result;
        }
        const numX = x * (z3x - 1) - y * z3y;
        const numY = x * z3y + y * (z3x - 1);
        sx = (numX * denX + numY * denY) / den;
        sy = (numY * denX - numX * denY) / den;
      } else {
        // NEWTON_MODE_SMALE: p(z) = z^3 - 2z + 2, p'(z) = 3z^2 - 2.
        // For a = 1, the inflection point z = 0 forms a superattracting 2-cycle 0 <-> 1.
        if (unrelaxed && r2 < 1e-4) {
          result.iterations = Math.ceil(maxIterations);
          result.escaped = false;
          result.mu = 0;
          result.root = 0;
          return result;
        }
        const z3x = x * (x2 - 3 * y2);
        const z3y = y * (3 * x2 - y2);
        const denX = 3 * (x2 - y2) - 2;
        const denY = 6 * x * y;
        const den = denX * denX + denY * denY;
        if (den <= 1e-28 || !Number.isFinite(den)) {
          result.iterations = Math.ceil(maxIterations);
          result.escaped = false;
          result.mu = 0;
          result.root = 0;
          return result;
        }
        const numX = z3x - 2 * x + 2;
        const numY = z3y - 2 * y;
        sx = (numX * denX + numY * denY) / den;
        sy = (numY * denX - numX * denY) / den;
      }
      x -= ax * sx - ay * sy;
      y -= ax * sy + ay * sx;
      iteration++;

      let bestD2 = Infinity;
      let bestK = 0;
      if (mode === NEWTON_MODE_CUBIC || mode === NEWTON_MODE_CENTERED_QUARTIC) {
        const d0 = (x - 1) * (x - 1) + y * y;
        const d1 = (x + 0.5) * (x + 0.5) + (y - SQRT3_2) * (y - SQRT3_2);
        const d2 = (x + 0.5) * (x + 0.5) + (y + SQRT3_2) * (y + SQRT3_2);
        bestD2 = d0;
        bestK = 0;
        if (d1 < bestD2) { bestD2 = d1; bestK = 1; }
        if (d2 < bestD2) { bestD2 = d2; bestK = 2; }
        if (mode === NEWTON_MODE_CENTERED_QUARTIC) {
          const d3 = x * x + y * y;
          if (d3 < bestD2) { bestD2 = d3; bestK = 3; }
        }
      } else if (mode === NEWTON_MODE_QUARTIC) {
        const d0 = (x - 1) * (x - 1) + y * y;
        const d1 = x * x + (y - 1) * (y - 1);
        const d2 = (x + 1) * (x + 1) + y * y;
        const d3 = x * x + (y + 1) * (y + 1);
        bestD2 = d0;
        bestK = 0;
        if (d1 < bestD2) { bestD2 = d1; bestK = 1; }
        if (d2 < bestD2) { bestD2 = d2; bestK = 2; }
        if (d3 < bestD2) { bestD2 = d3; bestK = 3; }
      } else if (mode === NEWTON_MODE_SEXTIC) {
        const d0 = Math.min((x - 1) * (x - 1) + y * y, (x + 1) * (x + 1) + y * y);
        const d1 = Math.min(
          (x - 0.5) * (x - 0.5) + (y - SQRT3_2) * (y - SQRT3_2),
          (x + 0.5) * (x + 0.5) + (y + SQRT3_2) * (y + SQRT3_2)
        );
        const d2 = Math.min(
          (x + 0.5) * (x + 0.5) + (y - SQRT3_2) * (y - SQRT3_2),
          (x - 0.5) * (x - 0.5) + (y + SQRT3_2) * (y + SQRT3_2)
        );
        bestD2 = d0;
        bestK = 0;
        if (d1 < bestD2) { bestD2 = d1; bestK = 1; }
        if (d2 < bestD2) { bestD2 = d2; bestK = 2; }
      } else {
        const d0 = (x - SMALE_R0X) * (x - SMALE_R0X) + y * y;
        const d1 = (x - SMALE_R1X) * (x - SMALE_R1X) + (y - SMALE_R1Y) * (y - SMALE_R1Y);
        const d2 = (x - SMALE_R1X) * (x - SMALE_R1X) + (y + SMALE_R1Y) * (y + SMALE_R1Y);
        bestD2 = d0;
        bestK = 0;
        if (d1 < bestD2) { bestD2 = d1; bestK = 1; }
        if (d2 < bestD2) { bestD2 = d2; bestK = 2; }
      }
      if (bestD2 < NEWTON_EPS_SQ) {
        result.iterations = iteration;
        result.escaped = true;
        result.root = bestK;
        result.mu = useMu ? computeNewtonSmoothMu(iteration, x, y, bestK, ax, ay, mode, bestD2) : 0;
        return result;
      }
    }
    result.iterations = iteration;
    result.escaped = false;
    result.mu = 0;
    result.root = 0;
    return result;
  }

  // Skip the known interior of the main cardioid and period-2 bulb. This keeps
  // the increased boundary detail budget inexpensive in overview images.
  if (isCardioidOrBulb(x0, y0)) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    if (result.root !== undefined) delete result.root;
    return result;
  }
  let x = 0, y = 0, x2 = 0, y2 = 0, iteration = 0;
  while (x2 + y2 <= 4 && iteration < maxIterations) {
    y = 2 * x * y + y0;
    x = x2 - y2 + x0;
    x2 = x * x;
    y2 = y * y;
    iteration++;
  }
  return finalizeEscape(result, iteration, x, y, x2, y2, x0, y0, useMu);
}

// Used only for compact views around the period-3 bulbs. The historical
// iterator above stays on the overview and exterior paths.
export function iterateUntilEscapePeriodic(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true, juliaC = null) {
  if (parseNewtonC(juliaC)) return iterateUntilEscape(x0, y0, result, maxIterations, useMu, juliaC);
  if (isCardioidOrBulb(x0, y0)) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    if (result.root !== undefined) delete result.root;
    return result;
  }
  let x = 0, y = 0, x2 = 0, y2 = 0, iteration = 0;
  let periodicX = 0, periodicY = 0, periodicCheckpoint = 32;
  while (x2 + y2 <= 4 && iteration < maxIterations) {
    y = 2 * x * y + y0;
    x = x2 - y2 + x0;
    x2 = x * x;
    y2 = y * y;
    iteration++;
    if (iteration >= 32 && x === periodicX && y === periodicY &&
        Object.is(x, periodicX) && Object.is(y, periodicY) && x2 + y2 <= 4) {
      // An identical finite state and identical c give the same future orbit.
      // Object.is distinguishes signed zero; === excludes NaN.
      result.iterations = Math.ceil(maxIterations);
      result.escaped = false;
      result.mu = 0;
      if (result.root !== undefined) delete result.root;
      return result;
    }
    if (iteration === periodicCheckpoint) {
      periodicX = x;
      periodicY = y;
      periodicCheckpoint *= 2;
    }
  }
  return finalizeEscape(result, iteration, x, y, x2, y2, x0, y0, useMu);
}

export const iterateUntilConvergence = iterateUntilEscape;

export function iterateUntilEscapeStripe(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true) {
  return iterateUntilEscapePatternMode(x0, y0, result, 'stripe', maxIterations, useMu);
}

export function iterateUntilEscapePatternMode(
  x0,
  y0,
  result,
  mode = 'stripe',
  maxIterations = MAX_ITERATIONS,
  useMu = true,
) {
  if (isCardioidOrBulb(x0, y0)) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    result.shade = 1;
    return result;
  }
  const highBailout = mode === 'stripe' || mode === 'binary_cells' || mode === 'normal_3d';
  const bailout = highBailout ? STRIPE_BAILOUT : ORBIT_TRAP_BAILOUT;
  const logBailout = highBailout ? LOG_STRIPE_BAILOUT : LOG_ORBIT_TRAP_BAILOUT;
  let x = 0, y = 0, x2 = 0, y2 = 0, iteration = 0;
  let dx = 0, dy = 0;
  let stripeSum = 0, prevStripe = 0, stripeCount = 0;
  let minStalk = 1e9;
  let minRing = 1e9;
  while (x2 + y2 <= bailout && iteration < maxIterations) {
    if (mode === 'normal_3d') {
      const ndx = 2 * (x * dx - y * dy) + 1;
      const ndy = 2 * (x * dy + y * dx);
      dx = ndx;
      dy = ndy;
    }
    y = 2 * x * y + y0;
    x = x2 - y2 + x0;
    x2 = x * x;
    y2 = y * y;
    iteration++;
    if (useMu && iteration >= 2) {
      if (mode === 'stripe') {
        const s = 0.5 * Math.sin(STRIPE_FREQUENCY * Math.atan2(y, x)) + 0.5;
        prevStripe = stripeSum;
        stripeSum += s;
        stripeCount++;
      } else if (mode === 'pickover_stalks') {
        const dAxis = Math.min(Math.abs(x), Math.abs(y));
        if (dAxis < minStalk) minStalk = dAxis;
      } else if (mode === 'cellular_web') {
        const dRing = Math.abs(Math.sqrt(x2 + y2) - 1.25);
        if (dRing < minRing) minRing = dRing;
      }
    }
  }
  result.iterations = iteration;
  result.escaped = x2 + y2 > bailout;
  result.mu = 0;
  result.shade = 1;
  if (result.escaped && useMu) {
    const logZn = 0.5 * Math.log(x2 + y2);
    const frac = 1 - Math.log(logZn / logBailout) * LOG2_INV;
    let mu = iteration + frac;
    let shade = 1;
    if (mode === 'stripe' && stripeCount >= 2) {
      const avg1 = stripeSum / stripeCount;
      const avg0 = prevStripe / (stripeCount - 1);
      const sa = avg0 + (avg1 - avg0) * Math.max(0, Math.min(1, frac));
      mu += 18 * sa;
      shade = 0.78 + 0.36 * sa;
    } else if (mode === 'binary_cells') {
      const ang = Math.atan2(y, x);
      const cellX = Math.sin(ang * 4);
      const cellY = Math.sin(frac * Math.PI * 2);
      const grid = cellX * cellY;
      const edge = Math.min(Math.abs(cellX), Math.abs(cellY));
      const border = Math.pow(Math.max(0, Math.min(1, edge / 0.18)), 0.45);
      if (grid > 0) mu += 6;
      shade = border * (grid > 0 ? 1.08 : 0.78);
    } else if (mode === 'normal_3d') {
      const dMag2 = dx * dx + dy * dy;
      if (dMag2 > 0) {
        const ux = (x * dx + y * dy) / dMag2;
        const uy = (y * dx - x * dy) / dMag2;
        const uMag = Math.hypot(ux, uy) || 1;
        const nx = ux / uMag;
        const ny = uy / uMag;
        const h = 1.35;
        const dot = (nx * -0.7071 + ny * -0.7071 + h) / Math.sqrt(1 + h * h);
        shade = Math.max(0.18, Math.min(1.35, 0.25 + 0.95 * Math.max(0, dot)));
      }
    } else if (mode === 'pickover_stalks') {
      const stalk = Math.exp(-minStalk * 28);
      mu += stalk * 24;
      shade = 0.72 + 0.55 * stalk;
    } else if (mode === 'cellular_web') {
      const web = Math.exp(-minRing * 22);
      mu += web * 20;
      shade = 0.70 + 0.55 * web;
    } else if (mode === 'equipotential_rings') {
      const wave = 0.5 + 0.5 * Math.cos(mu * Math.PI * 1.5);
      const ray = 0.5 + 0.5 * Math.cos(Math.atan2(y, x) * 8);
      const net = Math.max(Math.pow(wave, 6), Math.pow(ray, 8));
      mu += net * 10;
      shade = 0.72 + 0.48 * net;
    }
    result.mu = mu;
    result.shade = shade;
  }
  return result;
}
